import { Checkbox, FormHelperText, IconButton, Input } from '@mui/joy';
import { lowerFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { type DefaultName } from './FormikMultiPhoneNumbers.types';

import { DEFAULT_NAME, typesValues } from './FormikMultiPhoneNumbers.data';

import { usePhoneNumberLine, usePhoneNumberLines } from './FormikMultiPhoneNumbers.hooks';

import { DeleteIcon } from '@/shared/icons/Icons';
import FieldArrayContainer from '../../formik/FieldArrayContainer';
import FormField from '../FormField';
import InternationalPhoneNumber from '../../../phoneNumber/InternationalPhoneNumber';
import Selector from '../../select/Selector';

type InputLineProps = {
  fieldArrayName: string;
  index: number;
  onRemove: () => void;
};

const PhoneNumberInputLine: React.FC<InputLineProps> = ({ fieldArrayName, index, onRemove }) => {
  const { t } = useTranslation();

  const line = usePhoneNumberLine({ fieldArrayName, index });

  const typeOptions = typesValues.map((type) => ({
    label: t(`phoneNumbers.type.${lowerFirst(type)}`),
    value: type,
  }));

  return (
    <div className="grid grid-cols-[minmax(170px,3fr)_minmax(0,2fr)_minmax(0,4fr)_minmax(0,2fr)_auto] gap-2">
      <FormField error={line.phoneNumber.control.error} label={t('phoneNumbers.field.phoneNumber')}>
        <InternationalPhoneNumber
          callBack={line.phoneNumber.input.onChange}
          hasError={!!line.phoneNumber.control.error}
          value={line.phoneNumber.input.value}
          onBlur={line.phoneNumber.input.onBlur}
        />
      </FormField>
      <Selector
        {...line.type.control}
        {...line.type.input}
        label={t('phoneNumbers.field.type')}
        options={typeOptions}
      />
      <FormField {...line.remark.control} label={t('phoneNumbers.field.remark')}>
        <Input
          name={line.remark.input.name}
          value={line.remark.input.value ?? ''}
          onBlur={line.remark.input.onBlur}
          onChange={(e) => line.remark.input.onChange(e.target.value || null)}
        />
      </FormField>
      <FormField {...line.isDefault.control} className="mt-8">
        <Checkbox
          checked={line.isDefault.input.value}
          label={t('phoneNumbers.field.isDefault')}
          name={line.isDefault.input.name}
          onBlur={line.isDefault.input.onBlur}
          onChange={(e) => line.isDefault.input.onChange(e.target.checked)}
        />
      </FormField>
      <IconButton
        className="mt-6 aspect-square h-[1.875rem] w-full"
        color="danger"
        onClick={onRemove}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );
};

type ContainerProps<T extends string = DefaultName> = {
  className?: string;
  name?: T;
};

const FormikMultiPhoneNumbersInput = <T extends string = DefaultName>({
  name,
  ...rest
}: ContainerProps<T>) => {
  const fieldArrayName = name ?? DEFAULT_NAME;

  const [phoneNumberLines, lineHelpers] = usePhoneNumberLines({ name: fieldArrayName });

  const { t } = useTranslation();

  return (
    <FieldArrayContainer
      {...rest}
      name={fieldArrayName}
      renderInput={(fieldArray) => (
        <ul className="flex  w-full flex-col gap-2 p-1">
          {phoneNumberLines.map((line, index) => (
            <li key={line.inputId}>
              <PhoneNumberInputLine
                fieldArrayName={fieldArrayName}
                index={index}
                onRemove={() => lineHelpers.remove(fieldArray, index)}
              />
            </li>
          ))}
        </ul>
      )}
      renderFooter={(fieldArray) => {
        const linesError = lineHelpers.getGlobalError(fieldArray);

        return (
          !!linesError && (
            <FormHelperText className="mt-4 pl-2 text-xs text-red-500">{linesError}</FormHelperText>
          )
        );
      }}
      title={t('phoneNumbers.title')}
      onAdd={lineHelpers.push}
    />
  );
};

export default FormikMultiPhoneNumbersInput;
